import * as Sentry from "@sentry/react"
import { useMutation, useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, useRouter } from "@tanstack/react-router"
import { SavedPlate } from "ipctg-inflow-types/InFlowDB"
import { ContactType } from "ipctg-inflow-types/InFlowEnums"
import { useState } from "react"
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"

import api from "@/utilities/api"

import { Button } from "@/globalComponents/atoms/Button"
import Card from "@/globalComponents/atoms/Card"
import Checkbox from "@/globalComponents/atoms/Checkbox"
import FieldError from "@/globalComponents/atoms/FieldError"
import Heading from "@/globalComponents/atoms/Heading"
import Label from "@/globalComponents/atoms/Label"
import Text from "@/globalComponents/atoms/Text"
import ConfirmPlateModal from "@/globalComponents/molecules/ConfirmPlateModal"
import TermsAndConditions from "@/globalComponents/molecules/TermsAndConditions"
import TextField from "@/globalComponents/molecules/TextField"
import VehicleSelectorField from "@/globalComponents/molecules/VehicleSelectorField"
import PhoneOrEmailField, {
  PhoneOrEmailFieldValue,
} from "@/globalComponents/organisms/PhoneOrEmailField"
import { ExpectedError } from "@/utilities/expectedErrors"
import {
  licensePlateValidationRule,
  phoneOrEmailValidationRule,
  requiredRule,
} from "@/utilities/form"
import { meQueryOptions } from "@/utilities/hooks/queries/user"
import useToggle from "@/utilities/hooks/useToggle"
import { normalizeLicensePlate } from "@/utilities/normalizers"
import { toE164 } from "@/utilities/phone"
import { ParkPlateResponse } from "@/utilities/types/rsp"

import { LotMismatchWarningModal } from "../../passes.$epoch/-components/LotMismatchWarningModal"

import ActiveSessionModal from "./-components/ActiveSessionModal"

export const Route = createFileRoute("/qr/$qrLookupID/park/$epoch/register")({
  staticData: {
    pageName: "Register",
  },
  component: RouteComponent,
})

interface ParkRegisterFormSchema {
  selectedSavedPlate: string
  licensePlate: string
  contact: PhoneOrEmailFieldValue
  createAccount: boolean
}

// Helper function to get the selected plate from form data
function getSelectedPlate(data: ParkRegisterFormSchema): string {
  return data.selectedSavedPlate || data.licensePlate
}

function RouteComponent() {
  const navigate = Route.useNavigate()
  const router = useRouter()
  const { qrLookupID, epoch } = Route.useParams()
  const { t } = useTranslation(["errors", "gated", "common", "park"])
  const [showConfirmModal, toggleConfirmModal] = useToggle(false)
  const [showActiveSessionModal, toggleActiveSessionModal] = useToggle(false)
  const [showLotMismatchModal, toggleLotMismatchModal] = useToggle(false)
  const [canExtendSession, setCanExtendSession] = useState(false)
  const [lotMismatchData, setLotMismatchData] = useState<{
    qrLotName: string
    cameraLotName: string
  } | null>(null)
  const [pendingFormData, setPendingFormData] =
    useState<ParkRegisterFormSchema | null>(null)

  // Get user data
  const { data: me } = useSuspenseQuery(meQueryOptions())

  // Get saved vehicles from user data
  const savedVehicles: SavedPlate[] = me?.inflowUser.savedPlates || []

  const formValues: ParkRegisterFormSchema = {
    selectedSavedPlate: savedVehicles.length ? savedVehicles[0].plate : "",
    licensePlate: "",
    contact: {
      mode: "phone",
      value: {
        phone: me?.cognitoUser?.phone_number || "",
        email: me?.cognitoUser?.email || "",
      },
    },
    createAccount: true,
  }

  const form = useForm<ParkRegisterFormSchema>({
    defaultValues: formValues,
    values: formValues,
    resetOptions: {
      keepDirtyValues: true,
    },
    mode: "onBlur",
  })

  const [selectedSavedPlate] = useWatch({
    control: form.control,
    name: ["selectedSavedPlate"],
  })

  const { mutate: validatePlate, isPending } = useMutation({
    mutationFn,
    onSuccess,
    onError,
  })

  return (
    <>
      <Card>
        <Heading level={1} as={2}>
          {t("register.yourInfo", { ns: "park" })}
        </Heading>
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <fieldset disabled={isPending}>
              {savedVehicles.length > 0 && (
                <Controller
                  name="selectedSavedPlate"
                  control={form.control}
                  render={({ field }) => (
                    <VehicleSelectorField {...field}>
                      <Label>{t("register.savedPlates", { ns: "park" })}</Label>
                      {savedVehicles.map((vehicle) => (
                        <VehicleSelectorField.Radio
                          vehicle={vehicle}
                          key={`${vehicle.lastUsed}-${vehicle.nickname}-${vehicle.plate}`}
                        />
                      ))}
                      <VehicleSelectorField.Radio vehicle={undefined} />
                    </VehicleSelectorField>
                  )}
                />
              )}
              {(savedVehicles.length === 0 || selectedSavedPlate === "") && (
                <Controller
                  name="licensePlate"
                  control={form.control}
                  rules={{
                    required: requiredRule(
                      t("common.required", { ns: "errors" })
                    ),
                    validate: (value) => {
                      const normalized = normalizeLicensePlate(value)
                      return licensePlateValidationRule(t)(normalized)
                    },
                  }}
                  render={({
                    field: { ref, value, onChange, ...rest },
                    fieldState: { error, invalid },
                  }) => (
                    <TextField
                      {...rest}
                      isInvalid={invalid}
                      onChange={(v) => onChange(normalizeLicensePlate(v))}
                      value={typeof value === "string" ? value : ""}
                      onFocus={() => form.clearErrors("root")}
                      aria-label={t("register.licensePlate", { ns: "park" })}
                    >
                      <Label>
                        {t("register.licensePlate", { ns: "park" })}
                      </Label>
                      <TextField.Input
                        innerRef={ref}
                        placeholder="ABC123"
                        maxLength={12}
                        style={{ textTransform: "uppercase" }}
                      />
                      <FieldError>{error?.message}</FieldError>
                    </TextField>
                  )}
                />
              )}
              <Controller
                name="contact"
                control={form.control}
                rules={{
                  validate: phoneOrEmailValidationRule(t),
                }}
                render={({
                  field: { ref, ...rest },
                  fieldState: { error, invalid },
                }) => {
                  return (
                    <PhoneOrEmailField
                      innerRef={ref}
                      {...rest}
                      isInvalid={invalid}
                    >
                      <PhoneOrEmailField.Label />
                      <PhoneOrEmailField.Input innerRef={ref} />
                      <FieldError>{error?.message}</FieldError>
                    </PhoneOrEmailField>
                  )
                }}
              />
              {!me && (
                <Controller<ParkRegisterFormSchema, "createAccount">
                  name="createAccount"
                  render={({ field: { value, onChange, ref } }) => (
                    <Checkbox
                      id="createAccount"
                      innerRef={ref}
                      value={!!value}
                      onChange={onChange}
                      isDisabled={isPending}
                      style={{ marginTop: "var(--space-2)" }}
                    >
                      {t("register.contactInfo.createAccount", { ns: "gated" })}
                    </Checkbox>
                  )}
                />
              )}
              {form.formState.errors.root && (
                <FieldError className="center">
                  {form.formState.errors.root.message}
                </FieldError>
              )}

              <Button type="submit" buttonType="primary" isPending={isPending}>
                {isPending
                  ? t("register.validating", { ns: "park" })
                  : t("register.agreeAndContinue", { ns: "park" })}
              </Button>
              <Text level={3} className="center">
                <TermsAndConditions qrLookupID={qrLookupID} />
              </Text>
            </fieldset>
          </form>
        </FormProvider>
      </Card>
      <Text level={3}>{t("common.piUsage", { ns: "common" })}</Text>

      {/* Plate Confirmation Modal */}
      <ConfirmPlateModal
        isOpen={showConfirmModal}
        onOpenChange={toggleConfirmModal}
        plate={pendingFormData ? getSelectedPlate(pendingFormData) : ""}
        title={t("register.confirmPlate", { ns: "park" })}
        description={t("register.confirmPlateDescription", { ns: "park" })}
        editButtonText={t("register.editPlate", { ns: "park" })}
        confirmButtonText={t("register.continue", { ns: "park" })}
        onConfirm={handleConfirmPlate}
        onEdit={toggleConfirmModal}
      />

      {/* Active Session Modal */}
      <ActiveSessionModal
        isOpen={showActiveSessionModal}
        onOpenChange={toggleActiveSessionModal}
        canExtend={canExtendSession}
        onExtend={handleExtendSession}
        qrLookupID={qrLookupID}
      />

      {/* Lot Mismatch Warning Modal */}
      {lotMismatchData && (
        <LotMismatchWarningModal
          isOpen={showLotMismatchModal}
          onOpenChange={(isOpen) => {
            toggleLotMismatchModal()
            if (!isOpen) setLotMismatchData(null)
          }}
          qrLotName={lotMismatchData.qrLotName}
          cameraLotName={lotMismatchData.cameraLotName}
          onProceed={handleLotMismatchProceed}
        />
      )}
    </>
  )

  function handleExtendSession() {
    if (!pendingFormData) return

    // Close modal
    toggleActiveSessionModal()

    const plate = getSelectedPlate(pendingFormData)

    // If user wants to create an account and is not signed in, redirect to auth flow first
    if (pendingFormData.createAccount && !me) {
      const { href: ratesHref } = router.buildLocation({
        to: "/qr/$qrLookupID/park/$epoch/rates",
        params: { qrLookupID, epoch: epoch },
        search: {
          phone: pendingFormData.contact.value.phone,
          email: pendingFormData.contact.value.email,
          contactType:
            pendingFormData.contact.mode === "phone"
              ? ContactType.Phone
              : ContactType.Email,
          sessionEndDateTime: undefined,
          canExtend: undefined,
        },
      })

      navigate({
        to: "/auth",
        search: {
          qrLookupID,
          redirect: ratesHref,
          phone:
            pendingFormData.contact.mode === "phone"
              ? toE164(pendingFormData.contact.value.phone)
              : undefined,
          email:
            pendingFormData.contact.mode === "email"
              ? pendingFormData.contact.value.email
              : undefined,
          plate,
          conversionFlow: "parkRegister",
        },
      })
      return
    }

    // Navigate to rates for session extension
    navigate({
      to: "/qr/$qrLookupID/park/$epoch/rates",
      params: { qrLookupID, epoch: epoch },
      search: {
        phone: pendingFormData.contact.value.phone,
        email: pendingFormData.contact.value.email,
        contactType:
          pendingFormData.contact.mode === "phone"
            ? ContactType.Phone
            : ContactType.Email,
        sessionEndDateTime: undefined,
        canExtend: undefined,
      },
    })
  }

  function handleConfirmPlate() {
    if (!pendingFormData) return

    toggleConfirmModal()
    // Normal flow - validate plate and continue to rates
    validatePlate({ data: pendingFormData })
  }

  function handleLotMismatchProceed() {
    toggleLotMismatchModal()
    setLotMismatchData(null)
    if (pendingFormData) {
      validatePlate({ data: pendingFormData, skipLotMismatchWarning: true })
    }
  }

  function onSubmit(data: ParkRegisterFormSchema) {
    setPendingFormData(data)
    toggleConfirmModal()
  }

  async function mutationFn({
    data,
    skipLotMismatchWarning = false,
  }: {
    data: ParkRegisterFormSchema
    skipLotMismatchWarning?: boolean
  }) {
    const plate = getSelectedPlate(data)
    const response = await api.park.plate.POST({ plate })
    return { response, skipLotMismatchWarning }
  }

  function onSuccess(
    result: { response: ParkPlateResponse; skipLotMismatchWarning: boolean },
    variables: {
      data: ParkRegisterFormSchema
      skipLotMismatchWarning?: boolean
    }
  ) {
    const { response, skipLotMismatchWarning } = result
    if ("failReason" in response) return

    // Check for active session (already paid)
    if (response.alreadyPaid) {
      setPendingFormData(variables.data)
      setCanExtendSession(response.alreadyPaidData?.canExtendSession || false)
      toggleActiveSessionModal()
      return
    }

    // Check for lot mismatch warning
    if (!skipLotMismatchWarning && response.warning && response.warningData) {
      setPendingFormData(variables.data)
      setLotMismatchData({
        qrLotName: response.warningData.qrLotName,
        cameraLotName: response.warningData.cameraLotName,
      })
      toggleLotMismatchModal()
      return
    }

    const plate = getSelectedPlate(variables.data)

    // If user wants to create an account and is not signed in, redirect to auth flow
    if (variables.data.createAccount && !me) {
      const { href: ratesHref } = router.buildLocation({
        to: "/qr/$qrLookupID/park/$epoch/rates",
        params: { qrLookupID, epoch: epoch },
        search: {
          phone: variables.data.contact.value.phone,
          email: variables.data.contact.value.email,
          contactType:
            variables.data.contact.mode === "phone"
              ? ContactType.Phone
              : ContactType.Email,
          sessionEndDateTime: undefined,
          canExtend: undefined,
        },
      })

      navigate({
        to: "/auth",
        search: {
          qrLookupID,
          redirect: ratesHref,
          phone:
            variables.data.contact.mode === "phone"
              ? toE164(variables.data.contact.value.phone)
              : undefined,
          email:
            variables.data.contact.mode === "email"
              ? variables.data.contact.value.email
              : undefined,
          plate,
          conversionFlow: "parkRegister",
        },
      })
      return
    }

    navigate({
      to: "/qr/$qrLookupID/park/$epoch/rates",
      params: { qrLookupID, epoch: epoch },
      search: {
        phone: variables.data.contact.value.phone,
        email: variables.data.contact.value.email,
        contactType:
          variables.data.contact.mode === "phone"
            ? ContactType.Phone
            : ContactType.Email,
        sessionEndDateTime: undefined,
        canExtend: undefined,
      },
    })
  }

  function onError(
    error: Error,
    variables: {
      data: ParkRegisterFormSchema
      skipLotMismatchWarning?: boolean
    }
  ) {
    Sentry.captureException(error, {
      extra: {
        variables: variables.data,
        qrLookupID,
      },
      tags: {
        section: "park-register",
        flow: "plate-validation",
      },
    })

    if (error instanceof ExpectedError) {
      form.setError("licensePlate", {
        type: "server",
        message: t("register.errors.plateValidationFailed", { ns: "park" }),
      })
    } else {
      form.setError("root", {
        type: "server",
        message: t("common.somethingWentWrong", { ns: "errors" }),
      })
    }
  }
}
